import { FiLogOut } from "react-icons/fi";
import { NavLink, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Logo } from "../../../../common/components/logo";
import useLogout from "../../../../hooks/useLogout";
import { useAuthDetails } from "../../../../hooks/useAuthDetails";
import { KeyValueObj } from "../../../../common/types";
import { AdminSideItem } from "./admin-side-item";

import {
  sidebarItems,
  adminsideItems,
  pcnSideItems,
  stateMinistrySecretariat,
  pharmacyHeadSideItem,
  dgHealthSidebar,
  superAdminSideItems,
  hospitalAccountantSidebarItem,
  invitedUsers,
} from "./data";
import React, { Dispatch, SetStateAction } from "react";
import { MdOutlineClose } from "react-icons/md";
import { EUserRole } from "@/types/enum";

const DashSideWrapper = styled.div<{ showSidebar: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  transition: all 0.2s ease-in;
  padding: 2.75rem 1rem;
  background-color: ${({ theme }) => theme.bg.secondary};
  min-height: 100vh;
  width: 16rem;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;

  .icon-wrapper {
    width: 100%;
    justify-content: flex-end;
    display: none;

    .close-icon {
      color: red;
      width: 2rem;
      height: 2rem;
      background-color: #ffc20c21;
      border-radius: 50%;
      margin-top: -2rem;
    }

    @media (max-width: ${({ theme }) => theme.screen.mobile}) {
      display: flex;
    }
  }

  .logo-wrapper {
    text-decoration: none;
    width: 100%;
    .logo {
      margin-right: 1rem;
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
    }

    h2 {
      color: ${({ theme }) => theme.bg.secondary};
      font-weight: 700;
    }
  }

  .dashboard-items {
    width: 100%;
    align-items: flex-start;
    gap: 1rem;
    margin-top: 2rem;

    .dashboard-item {
      justify-content: flex-start;
      width: 100%;
    }
    .line-wrapper {
      width: 100%;
      padding-right: 2rem;
    }

    .line {
      width: 100%;
      height: 1px;
      background-color: ${({ theme }) => theme.text.muted};
    }

    .navlink {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 1rem;
      text-decoration: none;
      cursor: pointer;
      color: ${({ theme }) => theme.bg.prim_500};
      padding: 0.75rem 0.5rem;
      font-size: 1rem;
      border-radius: 0.25rem;
      :hover {
        background-color: ${({ theme }) => theme.bg.sec_100};
      }

      .dashboard-icon {
        font-size: 1rem;
        flex-shrink: 0;
        color: ${({ theme }) => theme.bg.prim_500};
        transition: all ease-in-out 0.5s;
      }

      .dashboard-wrapper {
        color: ${({ theme }) => theme.bg.prim_500};
        background: red;
      }

      h3 {
        font-weight: 400;
        font-size: 0.9rem;
        flex-shrink: 0;
        max-width: fit-content;
      }
    }

    .active-nav {
      background-color: ${({ theme }) => theme.bg.sec_100};
    }
  }

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    width: 18rem;
    height: 100vh;
    bottom: 0;
    top: 0;
    height: 100vh;
    left: ${({ showSidebar }) => (showSidebar ? 0 : "-18rem")};
    transition: left ease-in-out 0.3s;
  }

  .logo {
    padding: 0;
    margin: 0;
    transform: scale(1);
  }

  @media (max-width: 1024px) and (min-width: ${({ theme }) =>
      theme.screen.mobile}) {
    width: 5rem;
    overflow: hidden;
    :hover {
      width: 12.5rem;
      box-shadow: ${({ theme }) => theme.shadow.shad_100};
    }

    .dashboard-items .navlink {
      gap: 2rem;
      padding-left: 0.75rem;

      .dashboard-icon {
        font-size: 1.5rem;
      }
    }

    .logo {
      padding: 0;
      margin: 0;
      margin-left: -1.6rem;
      transform: scale(0.7);
    }
  }
`;

export interface IDashSidebarProps {
  showSidebar: boolean;
  setShowSidebar: Dispatch<SetStateAction<boolean>>;
}

export function DashboardSidebar({
  showSidebar,
  setShowSidebar,
}: IDashSidebarProps) {
  const logout = useLogout();
  const { role, companyType: sampleCompanyType } = useAuthDetails();
  const companyType = invitedUsers.includes(role?.toLowerCase())
    ? role
    : sampleCompanyType;
  function isEligible(roles: string[]): boolean {
    let eligible = false;
    roles?.forEach((eachRole: string) => {
      if (
        eachRole === "*" ||
        eachRole === role?.toLowerCase() ||
        eachRole === companyType?.toLowerCase()
      ) {
        eligible = true;
      }
    });
    return eligible;
  }
    const navigate = useNavigate();
  return (
    <DashSideWrapper showSidebar={showSidebar}>
      <div className="icon-wrapper">
        <MdOutlineClose
          onClick={() => setShowSidebar(false)}
          className="close-icon"
        />
      </div>
            <div 
        className="flex-row logo-wrapper cursor-pointer" 
        onClick={() => navigate('/dashboard/stats')}
      >
        <div className="logo">
          <Logo />
        </div>
      </div>
      <ul className="flex-col dashboard-items">
        {sidebarItems.map((item: KeyValueObj, i: number) => (
          <React.Fragment key={i}>
            {item.type === "line" &&
              companyType?.toLowerCase() !== "super admin" && (
                <div key={i} className="line-wrapper">
                  <div className="line"></div>
                </div>
              )}
            {item.type === "item" && isEligible(item.roles) ? (
              <li
                onClick={() => setShowSidebar(false)}
                key={i}
                className="flex-row dashboard-item"
              >
                <NavLink
                  className={({ isActive }) =>
                    isActive ? "navlink active-nav" : "navlink"
                  }
                  to={item.link}
                >
                  <item.icon className="dashboard-icon" />
                  <h3>{item.name}</h3>
                </NavLink>
              </li>
            ) : item.type === "dropdown" && isEligible(item.roles) ? (
              <AdminSideItem
                name={item.name}
                options={item.options}
                Icon={item.icon}
                setShowSidebar={setShowSidebar}
              />
            ) : null}
          </React.Fragment>
        ))}

        {pcnSideItems.map(
          (item: KeyValueObj, i: number) =>
            item.type === "item" &&
            isEligible(item.roles) && (
              <li
                onClick={() => setShowSidebar(false)}
                key={i}
                className="flex-row dashboard-item"
              >
                <NavLink
                  className={({ isActive }) =>
                    isActive ? "navlink active-nav" : "navlink"
                  }
                  to={item.link}
                >
                  <item.icon className="dashboard-icon" />
                  <h3>{item.name}</h3>
                </NavLink>
              </li>
            ),
        )}

        {/* Pharmacy head sidebar item */}
        {role?.toLowerCase() === EUserRole.HEAD_OF_PHARMACY &&
          pharmacyHeadSideItem.map(
            (item: KeyValueObj, i: number) =>
              item.type === "item" &&
              isEligible(item.roles) && (
                <li
                  onClick={() => setShowSidebar(false)}
                  key={i}
                  className="flex-row dashboard-item"
                >
                  <NavLink
                    className={({ isActive }) =>
                      isActive ? "navlink active-nav" : "navlink"
                    }
                    to={item.link}
                  >
                    <item.icon className="dashboard-icon" />
                    <h3>{item.name}</h3>
                  </NavLink>
                </li>
              ),
          )}

        {/* DG health side bar */}
        {role?.toLowerCase() === EUserRole.MINISTRY_OF_HEALTH_DG &&
          dgHealthSidebar.map(
            (item: KeyValueObj, i: number) =>
              item.type === "item" &&
              isEligible(item.roles) && (
                <li
                  onClick={() => setShowSidebar(false)}
                  key={i}
                  className="flex-row dashboard-item"
                >
                  <NavLink
                    className={({ isActive }) =>
                      isActive ? "navlink active-nav" : "navlink"
                    }
                    to={item.link}
                  >
                    <item.icon className="dashboard-icon" />
                    <h3>{item.name}</h3>
                  </NavLink>
                </li>
              ),
          )}

        {/* Hospital Accountant side bar item */}

        {role?.toLowerCase() === EUserRole.HOSPITAL_ACCOUNTANT &&
          hospitalAccountantSidebarItem?.map(
            (item: KeyValueObj, i: number) =>
              item.type === "item" &&
              isEligible(item.roles) && (
                <li
                  onClick={() => setShowSidebar(false)}
                  key={i}
                  className="flex-row dashboard-item"
                >
                  <NavLink
                    className={({ isActive }) =>
                      isActive ? "navlink active-nav" : "navlink"
                    }
                    to={item.link}
                  >
                    <item.icon className="dashboard-icon" />
                    <h3>{item.name}</h3>
                  </NavLink>
                </li>
              ),
          )}

        {/* Admin side bar items */}
        {role?.toLowerCase() === EUserRole.SUPER_ADMIN &&
          superAdminSideItems.map(
            (item: KeyValueObj, index: number) =>
              item.type === "item" && (
                <li
                  onClick={() => setShowSidebar(false)}
                  key={index}
                  className="flex-row dashboard-item"
                >
                  <NavLink
                    className={({ isActive }) =>
                      isActive ? "navlink active-nav" : "navlink"
                    }
                    to={item.link}
                  >
                    <item.icon className="dashboard-icon" />
                    <h3>{item.name}</h3>
                  </NavLink>
                </li>
              ),
          )}

        {role?.toLowerCase() === EUserRole.SUPER_ADMIN &&
          adminsideItems.map((item: KeyValueObj, index: number) => (
            <AdminSideItem
              key={index}
              name={item?.name}
              Icon={item?.icon}
              options={item?.options}
              setShowSidebar={setShowSidebar}
            />
          ))}

        {/* hospital head side bar items*/}

        {/* {role?.toLowerCase() === EUserRole.HOSPITAL_HEAD &&
          hospitalHeadSideItems?.map((item: any, index: number) => (
            <AdminSideItem
              key={index}
              name={item?.name}
              Icon={item?.icon}
              options={item?.options}
              setShowSidebar={setShowSidebar}
            />
          ))} */}

        {/* state ministry secretariat side bar items */}
        {role?.toLowerCase() === EUserRole.MINISTRY_OF_HEALTH_SECRETARY &&
          stateMinistrySecretariat?.map(
            (item: KeyValueObj, i: number) =>
              item.type === "item" &&
              isEligible(item.roles) && (
                <li
                  onClick={() => setShowSidebar(false)}
                  key={i}
                  className="flex-row dashboard-item"
                >
                  <NavLink
                    className={({ isActive }) =>
                      isActive ? "navlink active-nav" : "navlink"
                    }
                    to={item.link}
                  >
                    <item.icon className="dashboard-icon" />
                    <h3>{item.name}</h3>
                  </NavLink>
                </li>
              ),
          )}

        {/* Logout */}

        <li
          onClick={() => {
            setShowSidebar(false);
            logout();
          }}
          className="flex-row dashboard-item"
        >
          <div className="logout-wrapper navlink">
            <FiLogOut className="dashboard-icon" />
            <h3>Logout</h3>
          </div>
        </li>
      </ul>
    </DashSideWrapper>
  );
}
